import React, { useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { BACKEND_URL } from "@/config";
import { buildApiCandidates, shouldFailoverBackendRequest } from "@/backendBases";
import { KeyRound, Lock } from "lucide-react";

const API = BACKEND_URL;

/**
 * İlk giriş: müdür/süper adminin atadığı şifre ile girdikten sonra
 * yeni giriş şifresi ve 8 karakterli kurtarma parolası (harf/rakam; her biri iki kez) belirlenir.
 */
export default function FirstCredentialSetup({
  personnelName,
  onCompleted,
}) {
  const [newPassword, setNewPassword] = useState("");
  const [newPassword2, setNewPassword2] = useState("");
  const [parola, setParola] = useState("");
  const [parola2, setParola2] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 4) {
      toast.error("Şifre en az 4 karakter olmalıdır.");
      return;
    }
    if (newPassword !== newPassword2) {
      toast.error("Yeni şifre alanları eşleşmiyor.");
      return;
    }
    if (!/^[A-Za-z0-9]{8}$/.test(parola)) {
      toast.error("Kurtarma parolası tam 8 karakter olmalıdır (yalnızca harf ve rakam).");
      return;
    }
    if (parola !== parola2) {
      toast.error("Kurtarma parolası alanları eşleşmiyor.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        new_password: newPassword,
        new_password_confirm: newPassword2,
        new_parola: parola,
        new_parola_confirm: parola2,
      };
      let res = null;
      let lastErr = null;
      for (const base of buildApiCandidates(API)) {
        const url = base ? `${base}/api/auth/complete-credential-setup` : "/api/auth/complete-credential-setup";
        try {
          res = await axios.post(url, payload, { timeout: 20000 });
          break;
        } catch (err) {
          lastErr = err;
          if (shouldFailoverBackendRequest(err)) continue;
          const d = err.response?.data?.detail;
          const msg =
            typeof d === "string"
              ? d
              : Array.isArray(d)
                ? d.join(" ")
                : err.message || "İşlem başarısız.";
          toast.error(msg);
          return;
        }
      }
      if (!res) {
        const d = lastErr?.response?.data?.detail;
        const msg =
          typeof d === "string"
            ? d
            : Array.isArray(d)
              ? d.join(" ")
              : lastErr?.message || "Sunucuya bağlanılamadı.";
        toast.error(msg);
        return;
      }
      const data = res.data || {};
      if (data.token) {
        axios.defaults.headers.common["Authorization"] = `Bearer ${data.token}`;
        toast.success("Giriş bilgileriniz kaydedildi.");
        onCompleted({
          token: data.token,
          name: data.name,
          role: data.role,
          // F1-D: alias öncesi DB rolü (Başkan → "baskan"; role "admin" alias'ı).
          role_raw: data.role_raw,
          personnelId: data.personnel_id,
          access_expires_at: data.access_expires_at,
          idle_settings: data.idle_settings,
        });
      } else {
        toast.error("Yanıtta oturum bilgisi alınamadı.");
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      const msg =
        typeof d === "string"
          ? d
          : Array.isArray(d)
            ? d.join(" ")
            : err.message || "İşlem başarısız.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-emerald-50 to-green-100"
      data-testid="first-credential-setup"
    >
      <Card className="w-full max-w-lg shadow-xl border-green-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-green-900">
            <KeyRound className="h-6 w-6" />
            İlk giriş — şifre ve kurtarma parolası
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            {personnelName ? (
              <>
                Merhaba <strong>{personnelName}</strong>, yöneticinizin verdiği geçici şifre ile girdiniz. Devam
                etmek için kendi <strong>giriş şifrenizi</strong> ve ileride &quot;şifremi unuttum&quot; için
                kullanacağınız <strong>8 karakterlik kurtarma parolanızı</strong> (harf veya rakam) belirleyin.
              </>
            ) : (
              "Yöneticinizin verdiği geçici şifre ile girdiniz. Lütfen kalıcı şifre ve kurtarma parolası oluşturun."
            )}
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Lock className="h-4 w-4" /> Yeni giriş şifresi
              </Label>
              <Input
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="En az 4 karakter"
                minLength={4}
              />
              <Input
                type="password"
                autoComplete="new-password"
                value={newPassword2}
                onChange={(e) => setNewPassword2(e.target.value)}
                placeholder="Yeni şifre (tekrar)"
                minLength={4}
              />
            </div>
            <div className="space-y-2">
              <Label>Kurtarma parolası (8 karakter)</Label>
              <p className="text-xs text-muted-foreground">
                İleride şifrenizi unuttuğunuzda ad, soyad, telefon ile birlikte bu parolayı soracağız. Harf veya rakam,
                tam 8 karakter. <strong>Büyük ve küçük harf ayrı kabul edilir</strong> (A ≠ a).
              </p>
              <Input
                maxLength={8}
                autoComplete="off"
                value={parola}
                onChange={(e) => setParola(e.target.value.replace(/[^A-Za-z0-9]/g, "").slice(0, 8))}
                placeholder="Örn: ab12CD34"
              />
              <Input
                maxLength={8}
                autoComplete="off"
                value={parola2}
                onChange={(e) => setParola2(e.target.value.replace(/[^A-Za-z0-9]/g, "").slice(0, 8))}
                placeholder="Kurtarma parolası (tekrar)"
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Kaydediliyor..." : "Kaydet ve panele geç"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
